'use client';

import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Button as RizzuiButton, type ButtonProps as RizzuiButtonProps } from 'rizzui';
import cn from '@/utils/class-names';

export type ButtonProps = RizzuiButtonProps & {
  /** Label shown while the button is busy (e.g. "Saving…"). Optional. */
  loadingText?: React.ReactNode;
};

/**
 * App-wide button.
 *  - Visual states (focus ring, press feedback, disabled) are consistent everywhere.
 *  - ASYNC-AWARE: if `onClick` returns a Promise the button shows a spinner and ignores further
 *    clicks until it settles, so Save / Submit / Delete / Approve / Import actions cannot be
 *    double-fired. Synchronous handlers behave exactly as before.
 *  - `isLoading` can still be controlled by the caller (form submit state, redux flags, …).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, onClick, isLoading, disabled, loadingText, children, color = 'primary', ...props }, ref) => {
    const [pending, setPending] = useState(false);
    const mounted = useRef(true);
    useEffect(() => {
      mounted.current = true;
      return () => {
        mounted.current = false;
      };
    }, []);

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        if (pending) {
          event.preventDefault();
          return;
        }
        const result = (onClick as ((e: React.MouseEvent<HTMLButtonElement>) => unknown) | undefined)?.(event);
        if (result && typeof (result as Promise<unknown>).then === 'function') {
          setPending(true);
          const done = () => {
            if (mounted.current) setPending(false);
          };
          (result as Promise<unknown>).then(done, done);
        }
      },
      [onClick, pending]
    );

    const busy = Boolean(isLoading) || pending;

    return (
      <RizzuiButton
        ref={ref}
        isLoading={busy}
        disabled={disabled}
        aria-busy={busy || undefined}
        color={color}
        onClick={onClick ? handleClick : undefined}
        className={cn(
          'font-medium transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-out-strong',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2',
          'active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 disabled:active:scale-100',
          className
        )}
        {...props}
      >
        {busy && loadingText ? loadingText : children}
      </RizzuiButton>
    );
  }
);

Button.displayName = 'Button';
